import { DateQueryModel } from '../../Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { DateRangeApiModel, TimePeriodEnum } from '../../../api/shared/models/date-range-api.model';
import { DateRangeTypeApiEnum } from '../../../api/shared/enums/date-range-type-api.enum';
import { SearchCriteriaDateQueryModel } from '../../../api/mentions/model/search-criteria-date-query.model';
import moment from 'moment-timezone';
import { DateQueryDateTypeEnum, dateQueryDateTypeFromApiMap, dateQueryDateTypeToApiMap } from '../../../api/mentions/enum/date-query-date-type.enum';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';

// eslint-disable-next-line @typescript-eslint/naming-convention
const ReportDateRangeTypeEnumToDateQueryType: Map<DateRangeTypeApiEnum, DateQueryTypeEnum> = new Map([
    [DateRangeTypeApiEnum.ABSOLUTE, DateQueryTypeEnum.ABSOLUTE],
    [DateRangeTypeApiEnum.RELATIVE, DateQueryTypeEnum.RELATIVE],
    [DateRangeTypeApiEnum.RELATIVE_TIMEZONE, DateQueryTypeEnum.RELATIVE_TIMEZONE],
    [DateRangeTypeApiEnum.ALL, DateQueryTypeEnum.ALL],
]);

// eslint-disable-next-line @typescript-eslint/naming-convention
const DateQueryTypeToReportDateRangeTypeEnum: Map<DateQueryTypeEnum, DateRangeTypeApiEnum> = new Map([
    [DateQueryTypeEnum.ABSOLUTE, DateRangeTypeApiEnum.ABSOLUTE],
    [DateQueryTypeEnum.RELATIVE, DateRangeTypeApiEnum.RELATIVE],
    [DateQueryTypeEnum.RELATIVE_TIMEZONE, DateRangeTypeApiEnum.RELATIVE_TIMEZONE],
    [DateQueryTypeEnum.ALL, DateRangeTypeApiEnum.ALL],
]);

export class ReportDateRangeModel extends DateQueryModel {
    public absoluteRangeTimeZone: string;
    public timePeriod?: TimePeriodEnum;

    constructor(apiModel?: DateRangeApiModel) {
        super();

        if (apiModel) {
            this.type = ReportDateRangeTypeEnumToDateQueryType.get(apiModel.type);
            if (this.type === DateQueryTypeEnum.ABSOLUTE) {
                if (apiModel.absoluteRangeStart && apiModel.absoluteRangeEnd) {
                    this.absoluteRange = {
                        start: new Date(apiModel.absoluteRangeStart),
                        end: new Date(apiModel.absoluteRangeEnd)
                    };
                } else {
                    // @TODO: this code doesn't align anything by timezone
                    this.absoluteDate = new Date(apiModel.absoluteRangeStart);
                    this.absoluteRange = null;
                }
            } else if (this.type === DateQueryTypeEnum.RELATIVE) {
                this.relativeStartOffsetH = apiModel.relativeStartOffsetH;
                this.relativeEndOffsetH = apiModel.relativeEndOffsetH;
            } else if (this.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
                this.relativeTimezoneDateType = apiModel.relativeTimezoneDateTypeEnum;
                this.timezone = apiModel.timezone;
            } else {
                this.relativeStartOffsetH = null;
                this.relativeEndOffsetH = null;
            }
            // @TODO This is needed until we re-write the date filter component
            if (apiModel.absoluteRangeTimeZone) {
                this.absoluteRangeTimeZone = apiModel.absoluteRangeTimeZone;
            }

            if (apiModel.timePeriod) {
                this.timePeriod = apiModel.timePeriod;
            }

            if (apiModel.dateType) {
                this.dateType = dateQueryDateTypeFromApiMap.get(apiModel.dateType);
            } else {
                this.dateType = DateQueryDateTypeEnum.PUBLISHED;
            }

        } else {
            this.setDefaultDateRange();
        }
    }

    public toApiModel(): DateRangeApiModel {
        let start;
        let end;
        if (this.absoluteRange) {
            start = this.absoluteRange.start;
            end = this.absoluteRange.end;
        } else {
            start = moment(this.absoluteDate).startOf('day').toDate();
            end = 0;
        }
        return {
            type: DateQueryTypeToReportDateRangeTypeEnum.get(this.type),
            relativeTimezoneDateTypeEnum: this.relativeTimezoneDateType,
            timezone: this.timezone,
            relativeStartOffsetH: this.relativeStartOffsetH,
            relativeEndOffsetH: this.relativeEndOffsetH,
            absoluteRangeTimeZone: this.absoluteRangeTimeZone,
            absoluteRangeStart: +start,
            absoluteRangeEnd: +end,
            timePeriod: this.timePeriod,
            dateType: dateQueryDateTypeToApiMap.get(this.dateType)
        };
    }

    public toSearchQueryApiModel(): SearchCriteriaDateQueryModel {
        let apiDate: SearchCriteriaDateQueryModel = {
            relativeStartOffsetH: this.relativeStartOffsetH,
            relativeEndOffsetH: this.relativeEndOffsetH,
            dateType: this.type,
            relativeTimezoneDateType: this.relativeTimezoneDateType,
            timeZone: this.timezone
        } as SearchCriteriaDateQueryModel;

        if (this.absoluteRange) {
            apiDate.dateRange = {
                start: this.absoluteRange.start.getTime(),
                end: this.absoluteRange.end.getTime()
            };
        } else if (this.absoluteDate) {
            apiDate.dateRange = {
                start: moment(this.absoluteDate).startOf('day').toDate().getTime(),
                end: moment(this.absoluteDate).endOf('day').toDate().getTime()
            };
        }

        if (this.type === DateQueryTypeEnum.ALL || !this.type) {
            apiDate = null;
        }

        return apiDate;
    }

    public setFromDateQuery(dateQuery: DateQueryModel): ReportDateRangeModel {
        if (!dateQuery) {
            return this;
        }
        this.type = dateQuery.type;
        this.relativeTimezoneDateType = dateQuery.relativeTimezoneDateType;
        this.relativeEndOffsetH = dateQuery.relativeEndOffsetH;
        this.relativeStartOffsetH = dateQuery.relativeStartOffsetH;
        this.absoluteRange = dateQuery.absoluteRange;
        this.absoluteDate = dateQuery.absoluteDate;
        this.dateType = dateQuery.dateType;
        this.timezone = dateQuery.timezone;

        return this;
    }

    public toSearchCriteriaDateQueryModel(): SearchCriteriaDateQueryModel {
        switch (this.type) {
            case DateQueryTypeEnum.RELATIVE:
                return {
                    // this dateType in SearchCriteriaDateQueryModel is different from dateType in this class
                    dateType: DateQueryTypeEnum.RELATIVE,
                    relativeStartOffsetH: this.relativeStartOffsetH,
                    relativeEndOffsetH: this.relativeEndOffsetH
                } as SearchCriteriaDateQueryModel;

            case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                return {
                    // this dateType in SearchCriteriaDateQueryModel is different from dateType in this class
                    dateType: DateQueryTypeEnum.RELATIVE_TIMEZONE,
                    relativeTimezoneDateType: this.relativeTimezoneDateType,
                    timeZone: this.timezone
                } as SearchCriteriaDateQueryModel;
            case DateQueryTypeEnum.ABSOLUTE:
                if (this.absoluteRange) {
                    return {
                        dateType: DateQueryTypeEnum.ABSOLUTE,
                        dateRange: {
                            start: +this.absoluteRange.start,
                            end: +this.absoluteRange.end
                        }
                    } as SearchCriteriaDateQueryModel;
                } else {
                    return {
                        dateType: DateQueryTypeEnum.ABSOLUTE,
                        dateRange: {
                            start: +new Date(this.absoluteDate),
                            end: +moment(this.absoluteDate).add(1, 'day').subtract(1, 'millisecond').toDate()
                        }
                    } as SearchCriteriaDateQueryModel;
                }
            case DateQueryTypeEnum.ALL:
                return null;
        }
    }

    /**
     * Sets values to the default date range.
     */
    private setDefaultDateRange(): void {
        this.type = DateQueryTypeEnum.RELATIVE_TIMEZONE;
        this.relativeTimezoneDateType = RelativeTimezoneDateTypeEnum.LAST_24_HOURS;
    }

    /**
     * Compare this model to another of it's kind
     */
    public isEqual(other: ReportDateRangeModel): boolean {
        if (other === null) {
            return false;
        }

        if (this.type !== other.type) {
            return null;
        }

        if (this.type === DateQueryTypeEnum.ABSOLUTE) {
            if (this.absoluteRange) {
                if (+this.absoluteRange.start !== +other?.absoluteRange?.start) {
                    return false;
                }

                if (+this.absoluteRange.end !== +other?.absoluteRange?.end) {
                    return false;
                }
            } else {
                if (+this.absoluteDate !== +other?.absoluteDate) {
                    return false;
                }
            }
        } else if (this.type === DateQueryTypeEnum.RELATIVE) {
            if (this.relativeStartOffsetH !== other?.relativeStartOffsetH) {
                return false;
            }

            if (this.relativeEndOffsetH !== other?.relativeEndOffsetH) {
                return false;
            }
        } else if (this.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            if (this.relativeTimezoneDateType !== other?.relativeTimezoneDateType) {
                return false;
            }
        }

        if (this.dateType !== other.dateType) {
            return false;
        }

        return true;
    }

    setTimePeriod(timePeriod: TimePeriodEnum): void {
        this.timePeriod = timePeriod;
    }
}
